import { useMemo } from "react";

function toLocalDayKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function normalizeDayKeyFromSec(unixSec) {
  if (!unixSec) return null;
  const day = new Date(Number(unixSec) * 1000);
  day.setHours(0, 0, 0, 0);
  return toLocalDayKey(day);
}

function buildDateLookup(rawTasks) {
  const taskDateMap = {};
  const subtaskDateMap = {};

  const tasks = Array.isArray(rawTasks) ? rawTasks : [];
  tasks.forEach((task) => {
    const taskId = String(task?._id ?? "");
    if (taskId) {
      taskDateMap[taskId] = {
        start: task.Start ?? null,
        end: task.End ?? null,
        name: task.TaskName ?? task.Text ?? "Task",
      };
    }

    const subtasks = Array.isArray(task.TaskMap) ? task.TaskMap : [];
    subtasks.forEach((subtask) => {
      const subtaskId = String(subtask?._id ?? "");
      if (!subtaskId) return;

      subtaskDateMap[subtaskId] = {
        start: subtask.StartTime ?? task.Start ?? null,
        end: subtask.DeadLine ?? task.End ?? null,
        name:
          subtask.Text ??
          subtask.TaskName ??
          task.TaskName ??
          task.Text ??
          "Subtask",
      };
    });
  });

  return { taskDateMap, subtaskDateMap };
}

/**
 * Pure matrix used by Resource Timeline and resource shortage alerts.
 * Dates follow the same rules as the timeline (gantt + SpeceficTimes, subtask fallbacks).
 *
 * @param {Array} finalResources  board resources with attached TasksResources
 * @param {Array} rawTasks        gantt tasks (date lookup)
 * @param {Array} [manualAllocations] projected allocations with kind !== "task"
 *                                    (manual bookings + maintenance) — injected
 *                                    as per-day usage so they reduce availability
 */
export function buildResourceUsageMatrixData(
  finalResources,
  rawTasks,
  manualAllocations,
) {
  if (!Array.isArray(finalResources) || finalResources.length === 0) {
    return { usageMap: {}, workloadMap: {}, detailMap: {}, rangeBounds: null };
  }

  const { taskDateMap, subtaskDateMap } = buildDateLookup(rawTasks);
  const usageMap = {};
  const workloadMap = {};
  const detailMap = {};
  let minSec = Infinity;
  let maxSec = 0;

  function addToDay(resourceId, isoKey, amount, meta) {
    const { label, taskId, taskMapId } = meta;
    if (!usageMap[resourceId]) usageMap[resourceId] = {};
    usageMap[resourceId][isoKey] = (usageMap[resourceId][isoKey] ?? 0) + amount;
    workloadMap[resourceId] = (workloadMap[resourceId] ?? 0) + amount;

    if (!detailMap[resourceId]) detailMap[resourceId] = {};
    if (!detailMap[resourceId][isoKey]) detailMap[resourceId][isoKey] = [];

    const existing = detailMap[resourceId][isoKey].find(
      (entry) => entry.name === label,
    );
    if (existing) {
      existing.amount += amount;
    } else {
      detailMap[resourceId][isoKey].push({
        name: label,
        amount,
        taskId: taskId ?? null,
        taskMapId: taskMapId ?? null,
      });
    }
  }

  function getAssignmentDates(assignment) {
    const subtaskId = String(assignment?.TaskMapID ?? "");
    if (subtaskId && subtaskDateMap[subtaskId]) {
      return subtaskDateMap[subtaskId];
    }

    const taskId = String(assignment?.TaskID ?? "");
    if (taskId && taskDateMap[taskId]) {
      return taskDateMap[taskId];
    }

    return {
      start: null,
      end: null,
      name: assignment?.TaskMapName ?? assignment?.TaskName ?? "Task",
    };
  }

  function buildSpecificTimeOverrides(specificTimes, baseAmount) {
    const overrides = {};
    let minOverrideSec = Infinity;
    let maxOverrideSec = 0;

    const items = Array.isArray(specificTimes) ? specificTimes : [];
    items.forEach((specificTime) => {
      const overrideAmount = Number(specificTime?.Number) || baseAmount;
      const times = Array.isArray(specificTime?.Time) ? specificTime.Time : [];

      times.forEach((unixSec) => {
        const sec = Number(unixSec);
        if (!sec) return;
        const key = normalizeDayKeyFromSec(unixSec);
        if (!key) return;
        overrides[key] = overrideAmount;
        minOverrideSec = Math.min(minOverrideSec, sec);
        maxOverrideSec = Math.max(maxOverrideSec, sec);
      });
    });

    return {
      overrides,
      minOverrideSec: minOverrideSec === Infinity ? null : minOverrideSec,
      maxOverrideSec: maxOverrideSec || null,
    };
  }

  finalResources.forEach((resource) => {
    const resourceId = String(resource?._id ?? resource?.id ?? "");
    if (!resourceId) return;

    const assignments = Array.isArray(resource?.TasksResources)
      ? resource.TasksResources
      : [];
    assignments.forEach((assignment) => {
      const { start, end, name } = getAssignmentDates(assignment);
      const baseAmount = Number(assignment?.Number) || 1;
      const {
        overrides,
        minOverrideSec,
        maxOverrideSec,
      } = buildSpecificTimeOverrides(assignment?.SpeceficTimes, baseAmount);
      const label = assignment?.TaskMapID
        ? assignment?.TaskMapName ?? name
        : assignment?.TaskName ?? name;

      const effectiveStart =
        start && minOverrideSec
          ? Math.min(Number(start), minOverrideSec)
          : Number(start ?? minOverrideSec);
      const effectiveEnd =
        end && maxOverrideSec
          ? Math.max(Number(end), maxOverrideSec)
          : Number(end ?? maxOverrideSec);

      if (!effectiveStart || !effectiveEnd || effectiveStart > effectiveEnd) return;

      minSec = Math.min(minSec, effectiveStart);
      maxSec = Math.max(maxSec, effectiveEnd);

      const current = new Date(effectiveStart * 1000);
      current.setHours(0, 0, 0, 0);
      const finalDay = new Date(effectiveEnd * 1000);
      finalDay.setHours(0, 0, 0, 0);

      const taskId = assignment?.TaskID ? String(assignment.TaskID) : null;
      const taskMapId = assignment?.TaskMapID
        ? String(assignment.TaskMapID)
        : null;

      while (current <= finalDay) {
        const dayKey = toLocalDayKey(current);
        const amount = overrides[dayKey] ?? baseAmount;
        addToDay(resourceId, dayKey, amount, {
          label,
          taskId,
          taskMapId,
        });
        current.setDate(current.getDate() + 1);
      }
    });
  });

  // Inject manual bookings + maintenance blocks (they consume capacity too).
  const manual = Array.isArray(manualAllocations) ? manualAllocations : [];
  for (const alloc of manual) {
    if (alloc.status !== "active") continue;
    const resourceId = String(alloc.resourceId);
    const startMs = Number(alloc.start);
    const endMs = Number(alloc.end);
    if (!resourceId || !startMs || !endMs || endMs <= startMs) continue;

    const amount = Number(alloc.quantity) || 1;
    const label =
      alloc.reason ||
      (alloc.kind === "maintenance" ? "Maintenance" : "Manual booking");

    const current = new Date(startMs);
    current.setHours(0, 0, 0, 0);
    const finalDay = new Date(endMs - 1);
    finalDay.setHours(0, 0, 0, 0);

    minSec = Math.min(minSec, startMs / 1000);
    maxSec = Math.max(maxSec, endMs / 1000);

    while (current <= finalDay) {
      const dayKey = toLocalDayKey(current);
      addToDay(resourceId, dayKey, amount, {
        label,
        taskId: null,
        taskMapId: null,
      });
      current.setDate(current.getDate() + 1);
    }
  }

  return {
    usageMap,
    workloadMap,
    detailMap,
    rangeBounds:
      minSec === Infinity
        ? null
        : {
            minSec,
            maxSec,
          },
  };
}

/**
 * Calculates usage directly from `FinalResources[].TasksResources` plus any
 * manual bookings / maintenance blocks passed as `manualAllocations`.
 * Dates are resolved by matching `TaskID` / `TaskMapID` against gantt data.
 */
export function useResourceUsageMatrix(
  finalResources,
  rawTasks,
  manualAllocations,
) {
  return useMemo(
    () =>
      buildResourceUsageMatrixData(finalResources, rawTasks, manualAllocations),
    [finalResources, rawTasks, manualAllocations],
  );
}
